import { Link } from 'react-router-dom';
import { DocSection, Callout, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'five-tabs', label: 'The five tabs' },
  { id: 'top-bar', label: 'The top bar' },
  { id: 'app-vs-panel', label: 'Desktop app vs Premiere panel' },
];

export default function Body() {
  return (
    <>
      <DocSection id="five-tabs" title="The five tabs">
        <p>
          Everything in ClipRadar lives behind five tabs in a persistent top bar. Whatever you are
          doing, the same nav is one click away — and <span style={kbd}>Search</span> is where you
          land by default.
        </p>
        <DocShot
          slug="interface"
          shot="full-app"
          alt="The ClipRadar desktop app with the top navigation labeled"
          caption="The desktop app. The panel inside Premiere Pro is the same app in a narrower layout."
          callouts={[
            { n: 1, label: 'Search — find moments across your footage (Visuals / Speech / Ultra)' },
            { n: 2, label: 'Media — your library: files, folders, collections, analysis passes' },
            { n: 3, label: 'People — face clusters, named people, and Face Collections' },
            { n: 4, label: 'Settings — licensing, models, Ultra, Agent (MCP), and updates' },
            { n: 5, label: 'Help — docs, Discord, bug report, and the guided tour' },
            { n: 6, label: 'Backend-status pill — a live health dot for the local backend' },
            { n: 7, label: 'Update-available pill — click to jump to Settings and update' },
          ]}
        />
        <ul>
          <li>
            <strong><Link to="/docs/interface/search">Search</Link></strong> — find any moment by
            what is on screen, what is said, or who appears. The default tab.
          </li>
          <li>
            <strong><Link to="/docs/interface/media">Media</Link></strong> — your library: import
            files and folders, group them into collections, and run analysis.
          </li>
          <li>
            <strong><Link to="/docs/interface/people">People</Link></strong> — the auto-clustered
            faces you name once and then search for everywhere.
          </li>
          <li>
            <strong><Link to="/docs/interface/settings">Settings</Link></strong> — every
            configuration group, from licensing and models to Ultra Search and Agent (MCP).
          </li>
          <li>
            <strong><Link to="/docs/get-help">Help</Link></strong> — links to the docs and Discord,
            the in-app bug reporter, and buttons to replay onboarding or the guided tour.
          </li>
        </ul>
        <p>
          The active tab is remembered between launches, so ClipRadar reopens on whatever you were
          last doing.
        </p>
      </DocSection>

      <DocSection id="top-bar" title="The top bar">
        <p>Alongside the tabs, the top bar carries a few always-on indicators:</p>
        <ul>
          <li>
            <strong>Backend-status pill</strong> — a live health dot for the local backend that
            powers search and analysis. A healthy dot means the backend is ready.
          </li>
          <li>
            <strong>Update-available pill</strong> — appears when a desktop update is downloaded or
            the Premiere panel is out of date. Click it to jump straight to{' '}
            <Link to="/docs/interface/settings">Settings</Link> and finish updating.
          </li>
          <li>
            <strong>Install Premiere panel nudge</strong> (desktop only) — a small pill that takes
            you to the{' '}
            <Link to="/docs/platforms/premiere-panel">panel install</Link> step if you have not set
            it up yet.
          </li>
          <li>
            <strong>ClipRadar wordmark</strong> (desktop only) — dropped inside the panel to save
            horizontal space.
          </li>
        </ul>
        <p>
          Closing the desktop window does not quit ClipRadar — it keeps running in the{' '}
          <span style={kbd}>tray</span> so search and the Premiere panel stay available. See{' '}
          <Link to="/docs/platforms/standalone-app">The desktop app</Link> for how running in the
          tray works.
        </p>
      </DocSection>

      <DocSection id="app-vs-panel" title="Desktop app vs Premiere panel">
        <p>
          ClipRadar runs in two shells that share the exact same screens: the standalone{' '}
          <strong>desktop app</strong> on Windows, and the <strong>Premiere panel</strong> — the
          same app rendered inside Adobe Premiere Pro. The panel is a narrower, denser layout of
          every tab: the top nav auto-hides as you scroll and the wordmark is dropped, but the five
          tabs and their controls are the same.
        </p>
        <Callout kind="warn">
          A few Settings groups (Export, Storage, Ultra Search, Models) are{' '}
          <strong>read-only inside the panel</strong> — change them in the desktop app. Search and
          display preferences plus Licensing stay editable in both. Where a control behaves
          differently in the panel, each interface page notes it under <em>In the Premiere panel</em>.
        </Callout>
        <p>
          The two shells are covered in depth on{' '}
          <Link to="/docs/platforms/standalone-app">The desktop app</Link> and{' '}
          <Link to="/docs/platforms/premiere-panel">The Adobe Premiere Pro panel</Link>.
        </p>
      </DocSection>
    </>
  );
}
